<script setup lang="ts">
import {
  computeScheduleStatus,
  getBrowserTimezone,
  type FormSchedule,
} from "@/util/formSchedule";
import dayjs from "dayjs";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const props = defineProps<{
  schedule: FormSchedule;
}>();

const status = computed(() => computeScheduleStatus(props.schedule));

const hasStart = computed(
  () => props.schedule.open.mode === "date" && !!props.schedule.open.date
);
const hasEnd = computed(
  () => props.schedule.close.mode === "date" && !!props.schedule.close.date
);

// Flex weights: side segments = 1 (when present), middle = 1.6 (matches design).
const lf = computed(() => (hasStart.value ? 1 : 0));
const mf = 1.6;
const rf = computed(() => (hasEnd.value ? 1 : 0));
const tot = computed(() => lf.value + mf + rf.value);

const openPct = computed(() => (lf.value / tot.value) * 100);
const closePct = computed(() => ((lf.value + mf) / tot.value) * 100);

// Horizontal position of the "Current" flag = center of the active segment.
const nowCenter = computed(() => {
  if (status.value.state === "before") {
    return (lf.value / 2 / tot.value) * 100;
  }
  if (status.value.state === "closed") {
    return ((lf.value + mf + rf.value / 2) / tot.value) * 100;
  }
  return ((lf.value + mf / 2) / tot.value) * 100;
});

// Boundary times display with their timezone so a reviewer can confirm the
// value is in the selected schedule zone (the whole point of the feature),
// not the builder user's browser zone.
const tzLabel = computed(
  () => props.schedule.timezone || getBrowserTimezone()
);

const formatBoundary = (millis: number | null): string =>
  millis === null
    ? ""
    : `${dayjs(millis).format("MMM D, h:mm A")} (${tzLabel.value})`;

const opensSub = computed(() => formatBoundary(status.value.openAt));
const closesSub = computed(() => formatBoundary(status.value.closeAt));
</script>

<template>
  <div class="timeline">
    <div class="timeline__track">
      <!-- "Current" flag: dashed drop-line + chevron tag at the active position -->
      <div class="timeline__flag" :style="{ left: `${nowCenter}%` }">
        <span class="timeline__flag-line" />
        <span class="timeline__flag-tag">
          {{ t("form.scheduleVisibility.timeline.current") }}
        </span>
      </div>

      <!-- Segmented bar (single rounded container, internal borders) -->
      <div class="timeline__bar">
        <div
          v-if="hasStart"
          class="seg seg--side seg--left"
          :style="{ flexGrow: lf, flexBasis: 0 }"
        >
          <span class="seg__dot seg__dot--muted" />
          {{ t("form.scheduleVisibility.timeline.notOpenYet") }}
        </div>
        <div class="seg seg--open" :style="{ flexGrow: mf, flexBasis: 0 }">
          <span class="seg__dot seg__dot--open" />
          {{ t("form.scheduleVisibility.timeline.acceptingResponses") }}
        </div>
        <div
          v-if="hasEnd"
          class="seg seg--side seg--right"
          :style="{ flexGrow: rf, flexBasis: 0 }"
        >
          <span class="seg__dot seg__dot--muted" />
          {{ t("form.scheduleVisibility.timeline.closed") }}
        </div>
      </div>
    </div>

    <!-- Tick labels positioned at the segment boundaries (only when dated,
         so the fixed-height row doesn't reserve empty space otherwise). -->
    <div v-if="hasStart || hasEnd" class="timeline__ticks">
      <div v-if="hasStart" class="tick" :style="{ left: `${openPct}%` }">
        <span class="tick__mark" />
        <span class="tick__label">{{ t("form.scheduleVisibility.timeline.opens") }}</span>
        <span class="tick__sub">{{ opensSub }}</span>
      </div>
      <div v-if="hasEnd" class="tick" :style="{ left: `${closePct}%` }">
        <span class="tick__mark" />
        <span class="tick__label">{{ t("form.scheduleVisibility.timeline.closes") }}</span>
        <span class="tick__sub">{{ closesSub }}</span>
      </div>
    </div>
  </div>
</template>

<style scoped>
.timeline {
  max-width: 900px;
  /* No own margin — the parent body's flex gap controls spacing to the
     selectors below (avoids doubled space across all visible states). */
  margin: 0;
}

.timeline__track {
  position: relative;
  padding-top: 34px;
}

/* Current flag */
.timeline__flag {
  position: absolute;
  top: 0;
  transform: translateX(-50%);
  text-align: center;
  pointer-events: none;
  z-index: 5;
}

.timeline__flag-line {
  display: block;
  margin: 0 auto;
  height: 74px;
  border-left: 2px dashed #155eef;
}

.timeline__flag-tag {
  position: absolute;
  top: 0;
  left: 50%;
  background: #155eef;
  color: #ffffff;
  font-size: 11px;
  font-weight: 600;
  line-height: 15px;
  white-space: nowrap;
  padding: 3px 16px 3px 9px;
  clip-path: polygon(0 0, 100% 0, 84% 50%, 100% 100%, 0 100%);
}

/* Segmented bar */
.timeline__bar {
  display: flex;
  height: 42px;
  border: 1px solid #eaecf0;
  border-radius: 8px;
  overflow: hidden;
}

.seg {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  height: 100%;
  min-width: 0;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
}

.seg--side {
  background: #f9fafb;
  color: #475467;
}

.seg--left {
  border-right: 1px solid #eaecf0;
}

.seg--right {
  border-left: 1px solid #eaecf0;
}

.seg--open {
  background: #eff4ff;
  color: #155eef;
}

.seg__dot {
  flex: none;
  width: 6px;
  height: 6px;
  border-radius: 999px;
}

.seg__dot--muted {
  background: #98a2b3;
}

.seg__dot--open {
  background: #155eef;
}

/* Tick labels */
.timeline__ticks {
  position: relative;
  height: 40px;
  margin-top: 4px;
}

.tick {
  position: absolute;
  top: 0;
  transform: translateX(-50%);
  text-align: center;
  white-space: nowrap;
}

.tick__mark {
  display: block;
  width: 1px;
  height: 8px;
  margin: 0 auto 4px;
  background: #d0d5dd;
}

.tick__label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  color: #344054;
}

.tick__sub {
  display: block;
  font-size: 12px;
  line-height: 16px;
  color: #667085;
}
</style>
